import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import { FileText, QrCode, Landmark, X, CreditCard } from "lucide-react";
import {
  formatMoney,
  SALES_DOCUMENT_STATUS_LABELS,
  SALES_DOCUMENT_STATUS_TONE,
} from "@finora/config";
import { Button, StatusBadge, EmptyState } from "@finora/ui";
import { api } from "../../lib/api";

type IpsResult = { kind: "success" } | { kind: "failed"; responseCode: string | null };

/**
 * Javni pregled FAKTURE (bez naloga) - vidi razgovor "Moj računi". Za
 * razliku od PublicQuotePage, nema prihvatanje/odbijanje - samo pregled +
 * plaćanje (bankovni račun za ručni prenos, i IPS QR preko Payten PGW ako
 * je firma to aktivirala u Podešavanjima).
 */
export function PublicInvoicePage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  // ?ips=success|fail|cancel - rezervna informacija, za slučaj da korisnik
  // otvori plaćanje u posebnom prozoru (fallback ispod) umesto u modalu -
  // primarni, realtime ishod ide preko polling-a statusa transakcije dok je
  // modal otvoren (vidi ipsModalOpen/ipsTransactionId ispod).
  const ipsStatus = searchParams.get("ips");
  // ?otp=success|fail|cancel - vraća ga naš /payments/otp-card/callback
  // POSLE povratka sa banke (kupac je NAPUSTIO stranicu da unese podatke
  // kartice, pa jedini način da saznamo ishod jeste preko ovog parametra -
  // za razliku od IPS QR, ovde nema modala koji ostaje otvoren da pollujemo.
  const otpStatus = searchParams.get("otp");
  // ?intesa=success|fail i ?raiffeisen=success|fail - isti razlog kao otpStatus.
  const intesaStatus = searchParams.get("intesa");
  const raiffeisenStatus = searchParams.get("raiffeisen");

  const [ipsModalOpen, setIpsModalOpen] = useState(false);
  const [ipsQrCodeURL, setIpsQrCodeURL] = useState<string | null>(null);
  const [ipsTransactionId, setIpsTransactionId] = useState<string | null>(null);
  const [ipsResult, setIpsResult] = useState<IpsResult | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: invoice, isLoading, isError, refetch: refetchInvoice } = useQuery({
    queryKey: ["public-invoice", token],
    queryFn: () => api.getPublicInvoice(token!),
    enabled: !!token,
  });

  const payWithIps = useMutation({
    mutationFn: () => api.createPublicInvoiceIpsPayment(token!),
    onSuccess: (result) => {
      setIpsQrCodeURL(result.qrCodeURL);
      setIpsTransactionId(result.transactionId);
      setIpsResult(null);
      setIpsModalOpen(true);
    },
  });

  // Realtime status pokušaja plaćanja - vidi razgovor "cim se vrati odgovor
  // da se skloni i pokaze status... u realnom vremenu". Dok je modal
  // otvoren, pita backend na svake 2s da li je Payten-ov callback stigao
  // (SUCCESS/FAILED) - čim stigne, modal se sam zatvara i prikazuje ishod,
  // bez čekanja da korisnik ručno zatvori prozor/tab.
  useEffect(() => {
    if (!ipsModalOpen || !ipsTransactionId || !token) return;
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 300; // ~10 minuta na 2s

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const status = await api.getPublicIpsPaymentStatus(token, ipsTransactionId);
        if (cancelled) return;
        if (status.status === "SUCCESS") {
          setIpsModalOpen(false);
          setIpsResult({ kind: "success" });
          queryClient.setQueryData(["public-invoice", token], undefined);
          await refetchInvoice();
          return;
        }
        if (status.status === "FAILED") {
          setIpsModalOpen(false);
          setIpsResult({ kind: "failed", responseCode: status.responseCode });
          return;
        }
      } catch {
        // Mrežna greška pri pollovanju - ne prekidaj, probaj ponovo.
      }
      if (attempts < MAX_ATTEMPTS && !cancelled) {
        pollTimeoutRef.current = setTimeout(poll, 2000);
      }
    };
    poll();

    return () => {
      cancelled = true;
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ipsModalOpen, ipsTransactionId, token]);

  const payWithCard = useMutation({
    mutationFn: () => api.createPublicInvoiceOtpCardPayment(token!),
    onSuccess: (result) => {
      // Kupac MORA da napusti HAfiscal da unese podatke kartice na banci -
      // ovo je skrivena POST forma koja se odmah submituje, isti obrazac
      // kao dugme "Plati" u WHMCS "otp" gateway modulu (otp_link()).
      const form = document.createElement("form");
      form.method = "POST";
      form.action = result.gatewayUrl;
      form.style.display = "none";
      Object.entries(result.fields).forEach(([name, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = value;
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    },
  });

  // Deljena skrivena POST forma za kartična plaćanja (OTP/Intesa/Raiffeisen) -
  // kupac uvek napušta HAfiscal da unese podatke kartice na banci.
  function submitHiddenPaymentForm(gatewayUrl: string, fields: Record<string, string>) {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = gatewayUrl;
    form.style.display = "none";
    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
  }

  const payWithIntesaCard = useMutation({
    mutationFn: () => api.createPublicInvoiceIntesaCardPayment(token!),
    onSuccess: (result) => submitHiddenPaymentForm(result.gatewayUrl, result.fields),
  });

  const payWithRaiffeisenCard = useMutation({
    mutationFn: () => api.createPublicInvoiceRaiffeisenCardPayment(token!),
    onSuccess: (result) => submitHiddenPaymentForm(result.gatewayUrl, result.fields),
  });

  const dueAmount = invoice ? Number(invoice.dueAmount) : 0;
  // dueAmount <= 0 (posle refetch-a fakture) je dovoljno da pokrije i
  // "otp=success" povratak sa banke - callback registruje uplatu PRE nego
  // što preusmeri kupca nazad, pa je faktura već ažurirana kad se ova
  // stranica ponovo učita.
  const isFullyPaid = (!!invoice && dueAmount <= 0) || ipsResult?.kind === "success";

  return (
    <div className="min-h-screen bg-app-bg py-10">
      <div className="mx-auto max-w-3xl px-4">
        <div className="mb-6 flex items-center gap-2">
          <img src="/brand-logo.png" alt="HAFiskal" className="h-6 w-auto" />
        </div>

        {isLoading ? (
          <p className="text-sm text-slate-500">Učitavanje...</p>
        ) : isError || !invoice ? (
          <div className="rounded-xl border border-border bg-surface p-8 shadow-subtle">
            <EmptyState
              icon={FileText}
              title="Faktura nije pronađena"
              description="Link je možda netačan ili je faktura uklonjena."
            />
          </div>
        ) : (
          <div className="space-y-5">
            <div className="rounded-xl border border-border bg-surface p-6 shadow-subtle">
              <div className="mb-4 flex items-start justify-between">
                <div>
                  <h1 className="text-lg font-semibold text-slate-900">
                    Faktura {invoice.documentNumber ?? ""}
                  </h1>
                  <p className="text-sm text-slate-500">
                    {invoice.issuerCompanyName ? `Izdao: ${invoice.issuerCompanyName}` : ""}
                  </p>
                  <p className="text-sm text-slate-500">{invoice.customer?.name ?? "Poštovani kupče"}</p>
                </div>
                <StatusBadge
                  label={SALES_DOCUMENT_STATUS_LABELS[invoice.status]}
                  tone={SALES_DOCUMENT_STATUS_TONE[invoice.status]}
                />
              </div>

              <table>
                <thead>
                  <tr>
                    <th>Naziv</th>
                    <th>Količina</th>
                    <th>Cena</th>
                    <th>Ukupno</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((item) => (
                    <tr key={item.id}>
                      <td className="font-medium text-slate-800">{item.name}</td>
                      <td>
                        {item.quantity} {item.unit}
                      </td>
                      <td>{formatMoney(item.unitPriceGross)}</td>
                      <td className="font-medium">{formatMoney(item.grossAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="mt-4 flex justify-end">
                <div className="w-64 space-y-1 text-sm">
                  <div className="flex justify-between text-slate-500">
                    <span>Osnovica</span>
                    <span>{formatMoney(invoice.netAmount)}</span>
                  </div>
                  <div className="flex justify-between text-slate-500">
                    <span>PDV</span>
                    <span>{formatMoney(invoice.taxAmount)}</span>
                  </div>
                  {Number(invoice.paidAmount) > 0 && (
                    <div className="flex justify-between text-emerald-600">
                      <span>Plaćeno</span>
                      <span>{formatMoney(invoice.paidAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-slate-900">
                    <span>Za uplatu</span>
                    <span>{formatMoney(invoice.dueAmount)}</span>
                  </div>
                </div>
              </div>
            </div>

            {isFullyPaid ? (
              <div className="space-y-3">
                <p className="rounded-lg bg-emerald-50 px-4 py-3 text-center text-sm font-medium text-emerald-700">
                  Faktura je u potpunosti plaćena. Hvala Vam!
                </p>
                <Button variant="secondary" className="w-full" onClick={() => (window.location.href = "/")}>
                  Nazad na početnu
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {ipsResult?.kind === "failed" && (
                  <p className="rounded-lg bg-red-50 px-4 py-3 text-center text-sm font-medium text-red-700">
                    {ipsResult.responseCode === "EXPIRED" ? (
                      "Vreme za skeniranje QR koda je isteklo (3 minuta). Pokušajte ponovo ili platite uplatom na račun ispod."
                    ) : (
                      <>
                        Transakcija neuspešna.
                        {ipsResult.responseCode ? ` Kod greške: ${ipsResult.responseCode}.` : ""} Pokušajte ponovo
                        ili platite uplatom na račun ispod.
                      </>
                    )}
                  </p>
                )}
                {ipsStatus === "fail" && !ipsResult && (
                  <p className="rounded-lg bg-red-50 px-4 py-3 text-center text-sm font-medium text-red-700">
                    Plaćanje preko IPS QR koda nije uspelo. Pokušajte ponovo ili platite uplatom na račun
                    ispod.
                  </p>
                )}
                {ipsStatus === "cancel" && !ipsResult && (
                  <p className="rounded-lg bg-amber-50 px-4 py-3 text-center text-sm font-medium text-amber-700">
                    Otkazali ste plaćanje preko IPS QR koda. Možete pokušati ponovo ili platiti uplatom na
                    račun ispod.
                  </p>
                )}
                {otpStatus === "fail" && (
                  <p className="rounded-lg bg-red-50 px-4 py-3 text-center text-sm font-medium text-red-700">
                    Plaćanje karticom nije uspelo. Pokušajte ponovo ili platite uplatom na račun ispod.
                  </p>
                )}
                {otpStatus === "cancel" && (
                  <p className="rounded-lg bg-amber-50 px-4 py-3 text-center text-sm font-medium text-amber-700">
                    Otkazali ste plaćanje karticom. Možete pokušati ponovo ili platiti uplatom na račun
                    ispod.
                  </p>
                )}
                {intesaStatus === "fail" && (
                  <p className="rounded-lg bg-red-50 px-4 py-3 text-center text-sm font-medium text-red-700">
                    Plaćanje karticom nije uspelo. Pokušajte ponovo ili platite uplatom na račun ispod.
                  </p>
                )}
                {raiffeisenStatus === "fail" && (
                  <p className="rounded-lg bg-red-50 px-4 py-3 text-center text-sm font-medium text-red-700">
                    Plaćanje karticom nije uspelo. Pokušajte ponovo ili platite uplatom na račun ispod.
                  </p>
                )}
                <div className="rounded-xl border border-border bg-surface p-5 shadow-subtle">
                  <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
                    <Landmark className="h-4 w-4 text-slate-400" aria-hidden />
                    Plaćanje
                  </h2>

                  {invoice.bankAccountIban && (
                    <div className="mb-4 text-sm text-slate-600">
                      <p className="text-xs uppercase tracking-wide text-slate-400">Tekući račun (IBAN)</p>
                      <p className="font-mono text-base font-medium text-slate-900">
                        {invoice.bankAccountIban}
                      </p>
                      <p className="mt-1 text-xs text-slate-400">
                        Poziv na broj: {invoice.documentNumber ?? invoice.id}
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    {invoice.ipsPgwEnabled && (
                      <Button
                        className="w-full"
                        onClick={() => payWithIps.mutate()}
                        disabled={payWithIps.isPending}
                      >
                        <QrCode className="h-4 w-4" aria-hidden />
                        {payWithIps.isPending ? "Generisanje QR koda..." : "Plati IPS QR kodom"}
                      </Button>
                    )}

                    {invoice.otpCardEnabled && (
                      <Button
                        variant="secondary"
                        className="w-full"
                        onClick={() => payWithCard.mutate()}
                        disabled={payWithCard.isPending}
                      >
                        <CreditCard className="h-4 w-4" aria-hidden />
                        {payWithCard.isPending ? "Preusmeravanje na banku..." : "Plati platnom karticom (OTP Banka)"}
                      </Button>
                    )}

                    {invoice.intesaCardEnabled && (
                      <Button
                        variant="secondary"
                        className="w-full"
                        onClick={() => payWithIntesaCard.mutate()}
                        disabled={payWithIntesaCard.isPending}
                      >
                        <CreditCard className="h-4 w-4" aria-hidden />
                        {payWithIntesaCard.isPending ? "Preusmeravanje na banku..." : "Plati platnom karticom (Banca Intesa)"}
                      </Button>
                    )}

                    {invoice.raiffeisenCardEnabled && (
                      <Button
                        variant="secondary"
                        className="w-full"
                        onClick={() => payWithRaiffeisenCard.mutate()}
                        disabled={payWithRaiffeisenCard.isPending}
                      >
                        <CreditCard className="h-4 w-4" aria-hidden />
                        {payWithRaiffeisenCard.isPending ? "Preusmeravanje na banku..." : "Plati platnom karticom (Raiffeisen banka)"}
                      </Button>
                    )}
                  </div>

                  {payWithIps.isError && (
                    <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                      {payWithIps.error instanceof Error
                        ? payWithIps.error.message
                        : "Plaćanje trenutno nije moguće, pokušajte kasnije."}
                    </p>
                  )}
                  {payWithCard.isError && (
                    <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                      {payWithCard.error instanceof Error
                        ? payWithCard.error.message
                        : "Plaćanje karticom trenutno nije moguće, pokušajte kasnije."}
                    </p>
                  )}
                  {payWithIntesaCard.isError && (
                    <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                      {payWithIntesaCard.error instanceof Error
                        ? payWithIntesaCard.error.message
                        : "Plaćanje karticom trenutno nije moguće, pokušajte kasnije."}
                    </p>
                  )}
                  {payWithRaiffeisenCard.isError && (
                    <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                      {payWithRaiffeisenCard.error instanceof Error
                        ? payWithRaiffeisenCard.error.message
                        : "Plaćanje karticom trenutno nije moguće, pokušajte kasnije."}
                    </p>
                  )}
                </div>

                <Button variant="secondary" className="w-full" onClick={() => (window.location.href = "/")}>
                  Nazad na početnu
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {ipsModalOpen && ipsQrCodeURL && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setIpsModalOpen(false)}
        >
          <div
            className="flex h-[95vh] w-[95vw] max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-xl sm:h-[92vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h3 className="flex items-center gap-2 text-base font-semibold text-slate-800">
                <QrCode className="h-5 w-5 text-brand" aria-hidden />
                Skenirajte QR kod da platite
              </h3>
              <button
                onClick={() => setIpsModalOpen(false)}
                className="rounded-md p-1.5 text-slate-400 hover:bg-app-bg hover:text-slate-600"
                aria-label="Zatvori"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>
            <iframe
              src={ipsQrCodeURL}
              title="IPS QR plaćanje"
              className="w-full flex-1 border-0"
            />
            <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-2.5">
              <p className="text-xs text-slate-400">Čekanje na potvrdu plaćanja...</p>
              <a
                href={ipsQrCodeURL}
                target="_blank"
                rel="noreferrer"
                className="whitespace-nowrap text-xs font-medium text-brand hover:underline"
              >
                Otvori u novom prozoru
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
